import { apiConfig } from '../client/config';

/**
 * Media service – builds URLs for the User Media Controller getImage endpoint.
 * Use these URLs as img src so listing images load from the API.
 */
export class MediaService {
  private static readonly IMAGE_PATH = '/api/media/image';

  /** Matches any host's media proxy URL and captures the blob `src` query value. */
  private static readonly MEDIA_PROXY_PATTERN = /\/api\/media\/image\?src=(.+)$/i;

  /**
   * Returns the full URL for GET /api/media/image?src=...
   * Use as img src for listing thumbnails when the API returns an image id/path instead of a full URL.
   */
  static getImageUrl(src: string | number): string {
    const base = apiConfig.baseURL.replace(/\/$/, '');
    const path = MediaService.IMAGE_PATH;
    const encoded = encodeURIComponent(String(src));
    return `${base}${path}?src=${encoded}`;
  }

  /**
   * Extract blob/path src from an existing media proxy URL, if present.
   */
  static extractMediaSrc(value: string): string | null {
    const match = value.match(MediaService.MEDIA_PROXY_PATTERN);
    if (!match) return null;
    try {
      return decodeURIComponent(match[1]);
    } catch {
      return match[1];
    }
  }

  /**
   * Returns a URL suitable for img src.
   * Rewrites backend-embedded media proxy URLs to the configured API base (handles dev port/host drift).
   * Azure blob URLs are wrapped in the media proxy. Other absolute URLs are returned as-is.
   */
  static toImageSrc(value: string | number | null | undefined): string {
    if (value == null || value === '') return '';
    const s = String(value).trim();

    const embeddedSrc = MediaService.extractMediaSrc(s);
    if (embeddedSrc) {
      return MediaService.getImageUrl(embeddedSrc);
    }

    if (s.startsWith('http://') || s.startsWith('https://')) {
      if (MediaService.isBlobStorageUrl(s)) {
        return MediaService.getImageUrl(s);
      }
      return s;
    }

    return MediaService.getImageUrl(s);
  }

  private static isBlobStorageUrl(url: string): boolean {
    return /\.blob\.core\.windows\.net\//i.test(url);
  }
}
